'use client';

import { DialogBackdrop } from '@headlessui/react';
import { useSearchParams } from 'next/navigation';
import classNames from '@/utils/classNames';

import type { ModalBackdropProps } from './Modal.types';

export const MODAL_BACKDROP_QS = 'mb' as const;

const baseClassName = [
  'fixed',
  'inset-0',
  'isolate',
  'z-10',
  'bg-greyscale-950/30',
  'backdrop-blur-[1px]',
  'transition',
  'duration-100',
  'ease-out',
  'pointer-events-auto',
  // Shared for open and closed
  'group-data-closed/dialog:opacity-0',
  // Entering style
  'group-data-enter/dialog:group-data-closed:delay-100',
];

/**
 * Whether the current route asks for a backdrop-less modal. Drawers always
 * carry it, so it doubles as "the page behind this modal stays usable".
 */
export const useBackdropHidden = () =>
  useSearchParams().get(MODAL_BACKDROP_QS) === 'hidden';

const Backdrop = ({
  children,
  className,
  useQueryVariant = true,
  ...props
}: ModalBackdropProps) => {
  const backdropHidden = useBackdropHidden();
  const hidden = useQueryVariant && backdropHidden;

  if (hidden) {
    return null;
  }

  return (
    <DialogBackdrop
      data-backdrop
      className={classNames(baseClassName, className)}
      {...props}
    />
  );
};

export default Backdrop;
